import { z } from 'zod';
import {
  JsonValueSchema,
  NonEmptyStringSchema,
  TimestampSchema,
} from './common';
import { ManagedSemanticBehaviorSchema } from './managed-behavior';
import { StepHookSchema } from './step-hook';

/** Standard UI triggers for block event bindings (ADR 0004 D1). */
export const UITriggerEnum = z.enum([
  'onClick',
  'onView',
  'onSubmit',
  'onChange',
  'onClose',
  'onHover',
  'onTimeout',
]);

export const CustomUITriggerSchema = z
  .string()
  .regex(
    /^custom:[a-z0-9_]+$/,
    'Custom trigger must match custom:<snake_case>',
  );

export const UITriggerSchema = z.union([UITriggerEnum, CustomUITriggerSchema]);

/**
 * Canonical telemetry-transport namespace (ADR-0011). Authors and the
 * Storefront runtime speak plain names; transport emits `ui.<name>`.
 */
export const UI_TRANSPORT_PREFIX = 'ui.';

/** Legacy wire prefix accepted during the ADR-0011 compatibility window. */
export const LEGACY_FUNNEL_UI_TRANSPORT_PREFIX = 'funnel.ui.';

/**
 * @deprecated Use `UI_TRANSPORT_PREFIX`. Retained for source compatibility;
 * its value is the canonical `ui.` prefix.
 */
export const FUNNEL_UI_TRANSPORT_PREFIX = UI_TRANSPORT_PREFIX;

/** Canonical and compatibility wire-name regular expressions. */
export const UI_EVENT_NAME_RE = /^ui\.[a-z0-9_]+$/;
export const LEGACY_FUNNEL_UI_EVENT_NAME_RE =
  /^funnel\.ui\.[a-z0-9_]+$/;
export const UI_EVENT_COMPATIBLE_NAME_RE =
  /^(?:ui|funnel\.ui)\.[a-z0-9_]+$/;

/**
 * @deprecated Use `UI_EVENT_NAME_RE`. Retained for source compatibility; it
 * validates the canonical `ui.*` wire form.
 */
export const FUNNEL_UI_EVENT_NAME_RE = UI_EVENT_NAME_RE;

/**
 * Canonical PLAIN event name (D-G): a snake_case action with no transport
 * prefix. This is what authors type and what the runtime bus carries.
 */
export const PLAIN_EVENT_NAME_RE = /^[a-z][a-z0-9_]*$/;

/**
 * Reserved structural vocabulary. These names carry meaning in the wire
 * transition layer (`accept`/`decline`/…/`order_updated`) or the unified
 * DOM-leg listener namespace (`click`/`scroll`/`timer`/`page_view` — BL-46).
 * Of these, only `order_updated` is ALSO a legitimate behavioral catalog name
 * (owner 2026-07-15) — the rest must not be authorable as block-event binding
 * names, or an authored emission would collide with structural vocabulary.
 * Enforcement (D-G guardrail): `EventBindingNameSchema` hard-blocks
 * `HARD_RESERVED_EVENT_NAMES` (= this list minus catalog overlaps) for
 * AUTHORED bindings. Listeners may still SUBSCRIBE to any of these names
 * (that is the DOM-leg contract), and the runtime envelope stays permissive.
 */
export const RESERVED_EVENT_NAMES = [
  'accept',
  'decline',
  'page_view',
  'timeout',
  'cart_updated',
  'order_created',
  'order_updated',
  'click',
  'scroll',
  'scroll_depth',
  'timer',
  'timer_expired',
] as const;

/** Informational: true for ANY reserved name, including catalog overlaps like `order_updated`. */
export function isReservedEventName(name: string): boolean {
  return (RESERVED_EVENT_NAMES as readonly string[]).includes(
    normalizeEventName(name),
  );
}

/** Gate helper: true only for names that authored bindings must not use. */
export function isHardReservedEventName(name: string): boolean {
  return HARD_RESERVED_EVENT_NAMES.includes(normalizeEventName(name));
}

/**
 * Strip canonical `ui.` or legacy `funnel.ui.` prefixes so stored
 * bindings/listeners resolve to their plain runtime name.
 */
export function normalizeEventName(name: string): string {
  if (name.startsWith(LEGACY_FUNNEL_UI_TRANSPORT_PREFIX)) {
    return name.slice(LEGACY_FUNNEL_UI_TRANSPORT_PREFIX.length);
  }
  if (name.startsWith(UI_TRANSPORT_PREFIX)) {
    return name.slice(UI_TRANSPORT_PREFIX.length);
  }
  return name;
}

/** Build one canonical wire name from a plain, canonical, or legacy name. */
export function toWireEventName(name: string): string {
  return `${UI_TRANSPORT_PREFIX}${normalizeEventName(name)}`;
}

/** Strip either accepted transport prefix (consumer read path). */
export function fromWireEventName(name: string): string {
  return normalizeEventName(name);
}

export function isCompatibleUIWireEventName(name: string): boolean {
  return UI_EVENT_COMPATIBLE_NAME_RE.test(name);
}

/**
 * Canonicalize only an already-prefixed wire value. Plain values remain plain
 * and therefore fail `UIEventWireEventNameSchema`.
 */
export function canonicalizeUIWireEventName(name: string): string {
  return LEGACY_FUNNEL_UI_EVENT_NAME_RE.test(name)
    ? toWireEventName(name)
    : name;
}

const PlainNameBase = z
  .string()
  .min(1)
  .regex(
    PLAIN_EVENT_NAME_RE,
    'Event name must be a plain snake_case action (e.g. cta_clicked). ui. is the canonical telemetry-transport prefix; legacy funnel.ui. is accepted during migration.',
  );

/**
 * Plain event name (runtime bus / listener subscriptions). Canonical `ui.`
 * and legacy `funnel.ui.` names are accepted during the
 * compatibility window and normalized to plain form on read.
 */
export const PlainEventNameSchema = z.preprocess(
  (value) => (typeof value === 'string' ? normalizeEventName(value) : value),
  PlainNameBase,
);

/**
 * AUTHORED binding event name (block.events): plain + D-G guardrail — names in
 * `HARD_RESERVED_EVENT_NAMES` are rejected so an authored emission cannot
 * collide with wire-transition / DOM-leg structural vocabulary. `order_updated`
 * (catalog overlap, owner 2026-07-15) stays authorable.
 */
export const EventBindingNameSchema = z.preprocess(
  (value) => (typeof value === 'string' ? normalizeEventName(value) : value),
  PlainNameBase.superRefine((name, ctx) => {
    if (HARD_RESERVED_EVENT_NAMES.includes(name)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `"${name}" is reserved structural vocabulary (wire transition / DOM-leg listener namespace) and cannot be an authored binding name (D-G guardrail).`,
      });
    }
  }),
);

export const UIEventBindingSchema = z.object({
  name: EventBindingNameSchema,
  params: z.record(JsonValueSchema).optional(),
  paramsFrom: z.record(z.string()).optional(),
  condition: StepHookSchema.optional(),
  managedBehavior: ManagedSemanticBehaviorSchema.optional(),
});

export const BlockEventsSchema = z.record(
  z.union([UITriggerEnum, CustomUITriggerSchema]),
  z.array(UIEventBindingSchema),
);

export const UIEventScopeSchema = z
  .object({
    tenantId: NonEmptyStringSchema,
    storeId: NonEmptyStringSchema.optional(),
  })
  .strict();

export const UIEventContextSchema = z
  .object({
    funnelId: NonEmptyStringSchema,
    stepId: NonEmptyStringSchema,
    blockId: NonEmptyStringSchema,
    blockType: NonEmptyStringSchema,
    trigger: UITriggerSchema,
    pageType: NonEmptyStringSchema,
    currentUrl: NonEmptyStringSchema,
  })
  .strict();

export const UIEventPayloadSchema = z
  .object({
    params: z.record(JsonValueSchema),
    context: UIEventContextSchema,
  })
  .strict();

export const UIEventPhaseSchema = z.enum([
  'intent',
  'observation',
  'result',
]);

export const UIEventOriginSchema = z.enum([
  'block',
  'listener',
  'handler',
  'domain_authority',
  'transition_resolver',
]);

export const UIEventEnvelopeMetadataSchema = z
  .object({
    eventId: NonEmptyStringSchema,
    occurredAt: TimestampSchema,
    source: NonEmptyStringSchema,
    // Compatibility defaults are read-only migration behavior. New producers
    // validate with UIEventWriteEnvelopeSchema and must send both explicitly.
    phase: UIEventPhaseSchema.default('observation'),
    origin: UIEventOriginSchema.default('block'),
    correlationId: NonEmptyStringSchema.optional(),
    scope: UIEventScopeSchema,
    actor: z.record(JsonValueSchema).optional(),
  })
  .strict();

export const UIEventWriteEnvelopeMetadataSchema =
  UIEventEnvelopeMetadataSchema.extend({
    phase: UIEventPhaseSchema,
    origin: UIEventOriginSchema,
  });

export const UIEventEnvelopeSchema = z
  .object({
    // Runtime bus carries the PLAIN name (D-G); legacy prefixed names normalize.
    // Permissive on reserved names: the bus may carry structural re-emissions.
    name: PlainEventNameSchema,
    metadata: UIEventEnvelopeMetadataSchema,
    payload: UIEventPayloadSchema,
  })
  .strict();

export const UIEventWriteEnvelopeSchema = UIEventEnvelopeSchema.extend({
  metadata: UIEventWriteEnvelopeMetadataSchema,
});

export const UIEventWireEventNameSchema = z.preprocess(
  (value) =>
    typeof value === 'string' ? canonicalizeUIWireEventName(value) : value,
  z.string().regex(UI_EVENT_NAME_RE),
);

export const UIEventWireMetadataSchema = z
  .object({
    event_id: NonEmptyStringSchema,
    event_type: UIEventWireEventNameSchema,
    source: NonEmptyStringSchema,
    timestamp: TimestampSchema,
    phase: UIEventPhaseSchema.default('observation'),
    origin: UIEventOriginSchema.default('block'),
    store_id: NonEmptyStringSchema,
    tenant_id: NonEmptyStringSchema,
    correlation_id: NonEmptyStringSchema.optional(),
    session_id: NonEmptyStringSchema.optional(),
    user_id: NonEmptyStringSchema.optional(),
  })
  .strict();

export const UIEventWireWriteMetadataSchema = UIEventWireMetadataSchema.extend({
  phase: UIEventPhaseSchema,
  origin: UIEventOriginSchema,
});

export const UIEventWirePayloadSchema = z
  .object({
    metadata: UIEventWireMetadataSchema,
    data: UIEventPayloadSchema,
  })
  .strict()
  .refine((value) => value.metadata.phase !== 'intent', {
    path: ['metadata', 'phase'],
    message: 'Intent envelopes are local-only and cannot enter UI transport',
  });

export const UIEventWireWritePayloadSchema = z
  .object({
    metadata: UIEventWireWriteMetadataSchema,
    data: UIEventPayloadSchema,
  })
  .strict()
  .refine((value) => value.metadata.phase !== 'intent', {
    path: ['metadata', 'phase'],
    message: 'Intent envelopes are local-only and cannot enter UI transport',
  });

export const UIEventWireBatchSchema = z
  .object({
    events: z.array(UIEventWirePayloadSchema).max(100),
  })
  .strict();

export type UITrigger = z.infer<typeof UITriggerSchema>;
export type UIEventPhase = z.infer<typeof UIEventPhaseSchema>;
export type UIEventOrigin = z.infer<typeof UIEventOriginSchema>;
export type UIEventBinding = z.infer<typeof UIEventBindingSchema>;
export type BlockEvents = z.infer<typeof BlockEventsSchema>;
export type UIEventScope = z.infer<typeof UIEventScopeSchema>;
export type UIEventContext = z.infer<typeof UIEventContextSchema>;
export type UIEventPayload = z.infer<typeof UIEventPayloadSchema>;
export type UIEventEnvelopeMetadata = z.infer<
  typeof UIEventEnvelopeMetadataSchema
>;
export type UIEventEnvelope = z.infer<typeof UIEventEnvelopeSchema>;
export type UIEventWriteEnvelope = z.infer<
  typeof UIEventWriteEnvelopeSchema
>;
export type UIEventWireEventName = z.infer<
  typeof UIEventWireEventNameSchema
>;
export type UIEventWireMetadata = z.infer<typeof UIEventWireMetadataSchema>;
export type UIEventWirePayload = z.infer<typeof UIEventWirePayloadSchema>;
export type UIEventWireWritePayload = z.infer<
  typeof UIEventWireWritePayloadSchema
>;
export type UIEventWireBatch = z.infer<typeof UIEventWireBatchSchema>;

export function isUIEventTransportEligible(
  event: Pick<UIEventEnvelope, 'metadata'>,
): boolean {
  return event.metadata.phase !== 'intent';
}

export function canUIEventActivateListener(
  event: Pick<UIEventEnvelope, 'name' | 'metadata'>,
  listenerEventName: string,
): boolean {
  return !(
    event.metadata.phase === 'result' &&
    normalizeEventName(event.name) === normalizeEventName(listenerEventName)
  );
}

/**
 * Lint helper retained for builder autocomplete; the schema is authoritative.
 * Warns (never blocks) when a name is not a plain snake_case action. Canonical
 * and legacy wire prefixes normalize before checking.
 */
export function lintEventBindingName(name: string): string[] {
  const normalized = normalizeEventName(name);
  if (PLAIN_EVENT_NAME_RE.test(normalized)) {
    return [];
  }
  return [
    `Event name "${name}" should be a plain snake_case action (e.g. cta_clicked). ui. is applied only at the telemetry transport boundary; legacy funnel.ui. is accepted during migration.`,
  ];
}

/**
 * Well-known behavioral catalog for builder autocomplete (D3), PLAIN names
 * (D-G). The 07-15 owner additions (D-D commerce leg + `order_updated`) are at
 * the tail. `order_updated` intentionally overlaps the wire transition event.
 */
export const FUNNEL_UI_EVENT_CATALOG = [
  'cta_clicked',
  'lead_captured',
  'coupon_revealed',
  'spin_completed',
  'offer_accepted',
  'offer_declined',
  'experience_dismissed',
  'block_viewed',
  'form_submitted',
  'scroll_depth_reached',
  'exit_intent',
  // D-D commerce additions (owner 2026-07-15)
  'add_to_cart',
  'checkout_started',
  'order_completed',
  'order_updated',
  'variant_selected',
  'coupon_applied',
  'remove_from_cart',
] as const;

/**
 * Reserved names that are NOT legitimate catalog names — hard-blocked for
 * AUTHORED bindings by `EventBindingNameSchema` (D-G guardrail). Declared after
 * the catalog on purpose (module-init order).
 */
export const HARD_RESERVED_EVENT_NAMES = RESERVED_EVENT_NAMES.filter(
  (name) => !(FUNNEL_UI_EVENT_CATALOG as readonly string[]).includes(name),
) as readonly string[];

/**
 * Every event name whose semantics are owned by the TSS funnel runtime.
 *
 * The behavioral catalog is the only valid producer vocabulary for generated
 * Blueprint block bindings. Structural names are included here because a
 * listener may subscribe to runtime-owned events such as `page_view` or
 * `timeout`, even though a block must never author those emissions directly.
 */
export const TSS_KNOWN_FUNNEL_EVENT_NAMES = [
  ...FUNNEL_UI_EVENT_CATALOG,
  ...HARD_RESERVED_EVENT_NAMES,
] as readonly string[];

export function isKnownFunnelUIEventName(name: string): boolean {
  return (FUNNEL_UI_EVENT_CATALOG as readonly string[]).includes(
    normalizeEventName(name),
  );
}

export function isKnownTssFunnelEventName(name: string): boolean {
  return TSS_KNOWN_FUNNEL_EVENT_NAMES.includes(normalizeEventName(name));
}

/**
 * Catalog-constrained behavioral name for system-generated content. Generic
 * operator-authored `UIEventBindingSchema` remains open by design; this schema
 * is the stricter boundary used by portable Funnel Blueprints.
 */
export const KnownFunnelUIEventNameSchema = z.preprocess(
  (value) => (typeof value === 'string' ? normalizeEventName(value) : value),
  z.enum(FUNNEL_UI_EVENT_CATALOG),
);

/** Catalog events plus runtime-owned structural names, for Blueprint listeners. */
export const KnownTssFunnelEventNameSchema = z.preprocess(
  (value) => (typeof value === 'string' ? normalizeEventName(value) : value),
  PlainNameBase.refine(isKnownTssFunnelEventName, {
    message:
      'Event name must be a known TSS behavioral or structural funnel event',
  }),
);

/** Full UI binding shape with a catalog-constrained event name. */
export const KnownUIEventBindingSchema = UIEventBindingSchema.extend({
  name: KnownFunnelUIEventNameSchema,
});

/** Trigger-to-bindings map used when generated content must emit known events. */
export const KnownBlockEventsSchema = z.record(
  z.union([UITriggerEnum, CustomUITriggerSchema]),
  z.array(KnownUIEventBindingSchema),
);

export type KnownFunnelUIEventName = z.infer<
  typeof KnownFunnelUIEventNameSchema
>;
export type KnownTssFunnelEventName = z.infer<
  typeof KnownTssFunnelEventNameSchema
>;
export type KnownUIEventBinding = z.infer<typeof KnownUIEventBindingSchema>;
export type KnownBlockEvents = z.infer<typeof KnownBlockEventsSchema>;
